// Iter101 — LATM Admin > Orders (UI/UX restructure)
// -----------------------------------------------------------------
// Shopify-style order-management surface on top of the existing
// Iter100 backend endpoints. NO backend changes required.
//
//   • Header + search + FILTER button (dropdown) + APPLIED CHIP row
//   • Compact transaction table: Order # · Date · Customer · Channel
//                                · Total · Payment · Fulfillment · Items
//   • Right-side drawer (full-screen on mobile) for order detail with
//     the layered card structure: Fulfillment → Items → Payment/Totals
//     → Customer → Shipping → Admin Details (collapsible)
//   • More Actions menu → Reconcile Report, Viewed/No Buy, Refresh
//   • Human-friendly labels (`Paid`, `Pending`, `Ready` …)
//   • Server-side pagination (Showing 1-N of TOTAL · Prev/Next)

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Search, Filter as FilterIcon, MoreHorizontal, Eye, X,
  RefreshCw, Package, MapPin, CreditCard, User as UserIcon,
  ChevronDown, ChevronRight, ExternalLink, TruckIcon, AlertTriangle,
  ChevronLeft, ChevronsRight, Copy,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

// ── Display-friendly label map ─────────────────────────────────────
const PAY_LABEL = {
  PAID: "Paid", PARTIALLY_PAID: "Partially paid",
  PAYMENT_PENDING: "Pending", PAYMENT_FAILED: "Failed",
  DECLINED: "Declined", REFUNDED: "Refunded",
  PARTIALLY_REFUNDED: "Partial refund", CANCELED: "Canceled",
  UNKNOWN: "—",
};
const FUL_LABEL = {
  UNFULFILLED: "Unfulfilled", READY_TO_FULFILL: "Ready",
  PARTIALLY_FULFILLED: "Partially fulfilled",
  FULFILLED: "Fulfilled", SHIPPED: "Shipped",
  DELIVERED: "Delivered", CANCELED: "Canceled",
};
const SOURCE_LABEL = {
  shopify: "Shopify", lifted_core: "Lifted Core",
  partner: "Partner", manual: "Manual", legacy: "Legacy",
};
const PAY_TONE = {
  PAID: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  PARTIALLY_PAID: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  PAYMENT_PENDING: "bg-amber-400/15 text-amber-200 border-amber-400/40",
  PAYMENT_FAILED: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  DECLINED: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  REFUNDED: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  PARTIALLY_REFUNDED: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  CANCELED: "bg-white/10 text-lifted-dim border-white/10",
  UNKNOWN: "bg-white/10 text-lifted-dim border-white/10",
};
const FUL_TONE = {
  UNFULFILLED: "bg-white/10 text-lifted-dim border-white/10",
  READY_TO_FULFILL: "bg-sky-500/15 text-sky-200 border-sky-500/40",
  PARTIALLY_FULFILLED: "bg-amber-400/15 text-amber-200 border-amber-400/40",
  FULFILLED: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  SHIPPED: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  DELIVERED: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  CANCELED: "bg-white/10 text-lifted-dim border-white/10",
};
const SOURCE_TONE = {
  shopify: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  lifted_core: "bg-lifted-green/10 text-lifted-green border-lifted-green/30",
  partner: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  manual: "bg-white/10 text-lifted-dim border-white/10",
  legacy: "bg-white/10 text-lifted-dim border-white/10",
};

const PAY_OPTIONS  = ["PAID","PAYMENT_PENDING","PAYMENT_FAILED","DECLINED","REFUNDED","CANCELED"];
const FUL_OPTIONS  = ["UNFULFILLED","READY_TO_FULFILL","SHIPPED","FULFILLED"];
const SRC_OPTIONS  = ["shopify","lifted_core","partner"];
const PAGE_SIZES   = [25, 50, 100];

// ── helpers ────────────────────────────────────────────────────────
const money = (n) => (n == null || n === "" ? "—" : `$${Number(n).toFixed(2)}`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    const y = new Date(now); y.setDate(y.getDate() - 1);
    const yest = d.toDateString() === y.toDateString();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (same) return `Today ${time}`;
    if (yest) return `Yesterday ${time}`;
    return d.toLocaleDateString([], { month: "short", day: "numeric",
                                        year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  } catch { return String(iso).slice(0, 16).replace("T", " "); }
};
const fmtDateLong = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })
            + " · " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

function Chip({ children, tone, tid, small }) {
  return (
    <span data-testid={tid}
           className={`inline-block text-[0.65rem] leading-none tracking-[0.12em] font-bebas uppercase border rounded-full ${small ? "px-1.5 py-0.5" : "px-2 py-1"} ${tone || "border-white/10 text-lifted-dim"}`}>
      {children}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filter state
  const [q, setQ] = useState("");
  const [payFilters, setPayFilters] = useState([]);  // multi
  const [fulFilters, setFulFilters] = useState([]);
  const [srcFilters, setSrcFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [moreOpen, setMoreOpen]   = useState(false);
  const moreRef = useRef(null);

  // Detail drawer
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null); // full detail from GET /orders/{id}
  const [detailLoading, setDetailLoading] = useState(false);

  // "Reconcile Report" modal
  const [reconOpen, setReconOpen] = useState(false);

  // Applied filter chips (flatten)
  const appliedChips = useMemo(() => {
    const c = [];
    payFilters.forEach(v => c.push({ kind: "pay", value: v, label: PAY_LABEL[v] }));
    fulFilters.forEach(v => c.push({ kind: "ful", value: v, label: FUL_LABEL[v] }));
    srcFilters.forEach(v => c.push({ kind: "src", value: v, label: SOURCE_LABEL[v] }));
    return c;
  }, [payFilters, fulFilters, srcFilters]);

  const clearChip = (chip) => {
    if (chip.kind === "pay") setPayFilters(payFilters.filter(v => v !== chip.value));
    if (chip.kind === "ful") setFulFilters(fulFilters.filter(v => v !== chip.value));
    if (chip.kind === "src") setSrcFilters(srcFilters.filter(v => v !== chip.value));
  };
  const clearAllFilters = () => { setPayFilters([]); setFulFilters([]); setSrcFilters([]); };

  // Load orders (server-side pagination)
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      // Backend list endpoint takes single value per filter. If multiple
      // are selected we still request all (server) and filter client-side
      // — the underlying dataset is small (< 500). For very large ops
      // this can be lifted to server multi-select later.
      const { data } = await api.get("/admin/orders", {
        params: {
          q: q || undefined,
          payment_status: payFilters.length === 1 ? payFilters[0] : undefined,
          fulfillment_status: fulFilters.length === 1 ? fulFilters[0] : undefined,
          source: srcFilters.length === 1 ? srcFilters[0] : undefined,
          limit: 500, // grab a wide window; client paginates
        },
      });
      let all = data.orders || [];
      // Client-side multi-select
      if (payFilters.length > 1)
        all = all.filter(o => payFilters.includes(o.payment?.status));
      if (fulFilters.length > 1)
        all = all.filter(o => fulFilters.includes(o.fulfillment?.status));
      if (srcFilters.length > 1)
        all = all.filter(o => srcFilters.includes(o.order_source));
      setOrders(all);
      setTotal(all.length);
      setPage(1);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [q, payFilters, fulFilters, srcFilters]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  // Close More menu when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Load selected order detail
  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    setDetailLoading(true);
    api.get(`/admin/orders/${selectedId}`)
      .then(({ data }) => setSelected(data.order))
      .catch(e => setErr(apiErrorMessage(e)))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  // Slice for current page
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return orders.slice(start, start + pageSize);
  }, [orders, page, pageSize]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Operations"
      title="Orders"
      description="Customer orders across Shopify, Lifted Core and Partner sources."
      status={`${total} orders`}
      testId="admin-orders"
    >
      {/* ── Toolbar: search + Filter + More ─────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="orders-toolbar">
        <div className="glass-panel flex-1 min-w-[220px] px-3 py-2 flex items-center gap-2">
          <Search className="h-4 w-4 text-lifted-dim shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search orders, customers, email, tracking, SKU…"
                  className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-lifted-dim"
                  data-testid="orders-search" />
          {q && <button onClick={() => setQ("")} className="text-lifted-dim hover:text-white" aria-label="Clear search"><X className="h-3 w-3" /></button>}
        </div>
        <div className="relative">
          <button onClick={() => setFilterOpen(v => !v)}
                   className={`btn-lifted-outline text-xs inline-flex items-center gap-1 ${appliedChips.length ? "border-lifted-green text-lifted-green" : ""}`}
                   data-testid="orders-filter-toggle">
            <FilterIcon className="h-3 w-3" /> Filter {appliedChips.length > 0 && `(${appliedChips.length})`}
          </button>
          {filterOpen && (
            <FilterDropdown
              onClose={() => setFilterOpen(false)}
              payFilters={payFilters} setPayFilters={setPayFilters}
              fulFilters={fulFilters} setFulFilters={setFulFilters}
              srcFilters={srcFilters} setSrcFilters={setSrcFilters}
              clearAll={clearAllFilters}
            />
          )}
        </div>
        <div className="relative" ref={moreRef}>
          <button onClick={() => setMoreOpen(v => !v)}
                   className="btn-lifted-outline text-xs inline-flex items-center gap-1"
                   data-testid="orders-more-actions">
            <MoreHorizontal className="h-3 w-3" /> More
          </button>
          {moreOpen && (
            <div className="absolute right-0 top-full mt-1 z-30 min-w-[220px] rounded-lg border border-white/10 bg-lifted-black p-1 shadow-2xl"
                  data-testid="orders-more-menu">
              <MenuItem onClick={() => { setReconOpen(true); setMoreOpen(false); }} tid="more-reconcile">
                <AlertTriangle className="h-3 w-3" /> Reconcile Report
              </MenuItem>
              <MenuItem onClick={() => { load(); setMoreOpen(false); }} tid="more-refresh">
                <RefreshCw className="h-3 w-3" /> Refresh
              </MenuItem>
              <MenuItem onClick={() => { window.location.href = "/admin/abandoned-checkouts"; setMoreOpen(false); }} tid="more-abandoned" disabled>
                <ChevronsRight className="h-3 w-3" /> Abandoned Checkouts (soon)
              </MenuItem>
            </div>
          )}
        </div>
      </div>

      {/* Applied filter chip row */}
      {appliedChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="orders-applied-chips">
          {appliedChips.map((c, i) => (
            <button key={i} onClick={() => clearChip(c)}
                     className="inline-flex items-center gap-1 rounded-full border border-lifted-green/40 bg-lifted-green/10 text-lifted-green text-[0.65rem] font-bebas tracking-[0.14em] uppercase px-2 py-0.5 hover:bg-lifted-green/20">
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={clearAllFilters} className="text-[0.65rem] text-lifted-dim hover:text-white underline ml-1"
                   data-testid="orders-clear-all-filters">Clear all</button>
        </div>
      )}

      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm mb-2">{err}</div>}

      {/* ── Transaction table ───────────────────────────────────── */}
      <div className="glass-panel overflow-hidden" data-testid="orders-table">
        <table className="w-full text-sm">
          <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
            <tr className="border-b border-white/10">
              <th className="px-3 py-2.5 text-left">Order</th>
              <th className="px-3 py-2.5 text-left hidden md:table-cell">Date</th>
              <th className="px-3 py-2.5 text-left">Customer</th>
              <th className="px-3 py-2.5 text-left hidden lg:table-cell">Channel</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5 text-left">Payment</th>
              <th className="px-3 py-2.5 text-left">Fulfillment</th>
              <th className="px-3 py-2.5 text-right hidden md:table-cell">Items</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-3 py-8 text-center text-lifted-dim">Loading orders…</td></tr>)}
            {!loading && paged.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-lifted-dim">
                No orders match. {appliedChips.length > 0 && <button onClick={clearAllFilters} className="underline">Clear filters</button>}
              </td></tr>
            )}
            {paged.map(o => (
              <tr key={o.id}
                   onClick={() => setSelectedId(o.id)}
                   className="border-b border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors"
                   data-testid={`order-row-${o.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-anton text-white leading-tight">
                    {o.order_number ? `#${String(o.order_number).replace(/^#/, "")}` : `#${(o.id || "").slice(0, 6)}`}
                  </div>
                  <div className="md:hidden text-[0.6rem] text-lifted-dim">{fmtDate(o.created_at)}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-lifted-dim hidden md:table-cell">{fmtDate(o.created_at)}</td>
                <td className="px-3 py-2.5 text-xs">
                  <div className="text-white">{o.customer?.name || o.customer?.email || <span className="italic text-lifted-dim">Guest</span>}</div>
                  {o.customer?.email && o.customer?.name && (
                    <div className="text-[0.65rem] text-lifted-dim">{o.customer.email}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell">
                  <Chip small tone={SOURCE_TONE[o.order_source]} tid={`order-source-${o.id}`}>
                    {SOURCE_LABEL[o.order_source] || o.order_source}
                  </Chip>
                </td>
                <td className="px-3 py-2.5 text-right text-lifted-green font-bebas">{money(o.totals?.total)}</td>
                <td className="px-3 py-2.5">
                  <Chip small tone={PAY_TONE[o.payment?.status]} tid={`order-pay-status-${o.id}`}>
                    {PAY_LABEL[o.payment?.status] || o.payment?.status}
                  </Chip>
                </td>
                <td className="px-3 py-2.5">
                  <Chip small tone={FUL_TONE[o.fulfillment?.status]} tid={`order-ful-status-${o.id}`}>
                    {FUL_LABEL[o.fulfillment?.status] || o.fulfillment?.status}
                  </Chip>
                </td>
                <td className="px-3 py-2.5 text-right text-xs text-lifted-dim hidden md:table-cell">{(o.line_items || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination footer */}
        {total > 0 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 text-xs text-lifted-dim"
                data-testid="orders-pagination">
            <div>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1">
                Rows:
                <select value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}
                         className="bg-transparent border border-white/10 rounded px-1.5 py-0.5"
                         data-testid="orders-page-size">
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                       className="rounded border border-white/10 px-2 py-0.5 disabled:opacity-30"
                       data-testid="orders-prev-page"><ChevronLeft className="h-3 w-3" /></button>
              <span>{page} / {pageCount}</span>
              <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page >= pageCount}
                       className="rounded border border-white/10 px-2 py-0.5 disabled:opacity-30"
                       data-testid="orders-next-page"><ChevronRight className="h-3 w-3" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Order detail drawer */}
      {selectedId && (
        <OrderDrawer
          order={selected}
          loading={detailLoading}
          onClose={() => setSelectedId(null)}
          onRefresh={async () => {
            try {
              const { data } = await api.post(`/admin/orders/${selectedId}/reconcile-shopify`);
              if (data.ok) setSelected(data.order);
              else setErr(data.message || "Could not reconcile.");
            } catch (e) { setErr(apiErrorMessage(e)); }
          }}
          onFulfilled={() => { load(); }}
        />
      )}

      {/* Reconcile Report modal */}
      {reconOpen && <ReconcileReportModal onClose={() => setReconOpen(false)} />}
    </AdminFeatureShell>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Reusable menu item
function MenuItem({ children, onClick, tid, disabled }) {
  return (
    <button disabled={disabled} onClick={onClick} data-testid={tid}
             className="w-full text-left text-xs px-2.5 py-1.5 rounded flex items-center gap-2 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed">
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FILTER DROPDOWN — checkbox groups + apply
function FilterDropdown({ onClose, payFilters, setPayFilters, fulFilters, setFulFilters,
                            srcFilters, setSrcFilters, clearAll }) {
  const toggle = (arr, setter, v) =>
    setter(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div className="absolute right-0 top-full mt-1 z-30 w-[300px] rounded-lg border border-white/10 bg-lifted-black p-4 shadow-2xl space-y-4"
            data-testid="orders-filter-dropdown">
        <FilterGroup title="Payment" options={PAY_OPTIONS} labelMap={PAY_LABEL} value={payFilters}
                       onToggle={(v) => toggle(payFilters, setPayFilters, v)} testPrefix="filter-pay" />
        <FilterGroup title="Fulfillment" options={FUL_OPTIONS} labelMap={FUL_LABEL} value={fulFilters}
                       onToggle={(v) => toggle(fulFilters, setFulFilters, v)} testPrefix="filter-ful" />
        <FilterGroup title="Source" options={SRC_OPTIONS} labelMap={SOURCE_LABEL} value={srcFilters}
                       onToggle={(v) => toggle(srcFilters, setSrcFilters, v)} testPrefix="filter-src" />
        <div className="flex justify-between pt-2 border-t border-white/10">
          <button onClick={clearAll} className="text-[0.65rem] text-lifted-dim hover:text-white underline"
                   data-testid="filter-clear-all">Clear all</button>
          <button onClick={onClose} className="btn-lifted text-xs" data-testid="filter-done">Done</button>
        </div>
      </div>
    </>
  );
}
function FilterGroup({ title, options, labelMap, value, onToggle, testPrefix }) {
  return (
    <div>
      <div className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(v => {
          const active = value.includes(v);
          return (
            <button key={v} onClick={() => onToggle(v)}
                     data-testid={`${testPrefix}-${v.toLowerCase()}`}
                     className={`text-[0.65rem] font-bebas tracking-[0.14em] uppercase border rounded-full px-2 py-0.5 ${active ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-lifted-dim hover:text-white"}`}>
              {labelMap[v] || v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ORDER DRAWER — right-side slide-in, full screen on mobile
function OrderDrawer({ order, loading, onClose, onRefresh, onFulfilled }) {
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const shopUrl = order?.shopify_order_id && order?.order_source === "shopify"
    ? `https://${(order?.shopify_checkout_url || "").split("/").slice(2, 3)[0] || "admin.shopify.com"}/admin/orders/${String(order.shopify_order_id).replace(/^gid:.*\//, "")}`
    : null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="order-drawer-overlay" />
      <aside className="fixed inset-y-0 right-0 z-50 w-full lg:w-[640px] xl:w-[720px] bg-lifted-black border-l border-white/10 shadow-2xl flex flex-col"
              data-testid="order-detail">
        {loading || !order ? (
          <div className="p-8 text-lifted-dim">Loading order…</div>
        ) : (
          <>
            {/* ── Drawer header ──────────────────────────────── */}
            <header className="shrink-0 px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3 bg-lifted-black">
              <div>
                <button onClick={onClose} className="text-[0.65rem] tracking-[0.18em] font-bebas text-lifted-dim hover:text-white flex items-center gap-1 mb-1"
                         data-testid="order-drawer-back">
                  <ChevronLeft className="h-3 w-3" /> Back to Orders
                </button>
                <h2 className="font-anton text-2xl text-white leading-none">
                  Order {order.order_number ? `#${String(order.order_number).replace(/^#/, "")}` : `#${(order.id || "").slice(0, 8)}`}
                </h2>
                <div className="text-[0.7rem] text-lifted-dim mt-0.5">
                  {fmtDateLong(order.created_at)}
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap items-center">
                  <Chip tone={PAY_TONE[order.payment?.status]} tid="order-drawer-pay">{PAY_LABEL[order.payment?.status] || order.payment?.status}</Chip>
                  <Chip tone={FUL_TONE[order.fulfillment?.status]} tid="order-drawer-ful">{FUL_LABEL[order.fulfillment?.status] || order.fulfillment?.status}</Chip>
                  <Chip tone={SOURCE_TONE[order.order_source]} tid="order-drawer-source">{SOURCE_LABEL[order.order_source] || order.order_source}</Chip>
                </div>
              </div>
              <button onClick={onClose} className="text-lifted-dim hover:text-white shrink-0" aria-label="Close" data-testid="order-drawer-close">
                <X className="h-5 w-5" />
              </button>
            </header>

            {/* ── Drawer body (scrollable) ───────────────────── */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="order-drawer-body">
              {/* Actions */}
              <div className="flex flex-wrap gap-2" data-testid="order-drawer-actions">
                {order.payment?.status === "PAID" && order.fulfillment?.status !== "FULFILLED" && order.fulfillment?.status !== "SHIPPED" && (
                  <button onClick={() => setFulfillOpen(v => !v)}
                           className="btn-lifted text-xs inline-flex items-center gap-1"
                           data-testid="order-fulfill-toggle">
                    <TruckIcon className="h-3 w-3" /> {fulfillOpen ? "Close" : "Fulfill Order"}
                  </button>
                )}
                <button onClick={onRefresh}
                         className="btn-lifted-outline text-xs inline-flex items-center gap-1"
                         title="Read-only fetch from Shopify — never charges or cancels"
                         data-testid="order-refresh-shopify">
                  <RefreshCw className="h-3 w-3" /> Refresh from Shopify
                </button>
                {shopUrl && (
                  <a href={shopUrl} target="_blank" rel="noreferrer"
                      className="btn-lifted-outline text-xs inline-flex items-center gap-1"
                      data-testid="order-view-in-shopify">
                    <ExternalLink className="h-3 w-3" /> View in Shopify
                  </a>
                )}
              </div>

              {fulfillOpen && <FulfillmentForm order={order}
                                                   onDone={() => { setFulfillOpen(false); onFulfilled && onFulfilled(); }} />}

              {/* FULFILLMENT / ITEMS CARD */}
              <Card title={`Fulfillment · ${(order.line_items || []).length} items`} tid="section-items"
                     right={<Chip small tone={FUL_TONE[order.fulfillment?.status]}>{FUL_LABEL[order.fulfillment?.status]}</Chip>}>
                <ul className="divide-y divide-white/5">
                  {(order.line_items || []).map((it, i) => (
                    <li key={i} className="py-3 flex gap-3" data-testid={`order-item-${i}`}>
                      {it.image_url ? (
                        <img src={it.image_url} alt="" className="w-16 h-16 object-cover rounded border border-white/10 shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="w-16 h-16 rounded border border-white/10 bg-black/40 flex items-center justify-center text-[0.55rem] text-lifted-dim shrink-0">IMG</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{it.title || it.handle || "(untitled)"}</div>
                        {it.variant_title && <div className="text-[0.65rem] text-lifted-green font-bebas tracking-[0.14em] uppercase mt-0.5">{it.variant_title}</div>}
                        {it.sku && <div className="text-[0.65rem] text-lifted-dim font-mono">SKU: {it.sku}</div>}
                        <div className="text-[0.65rem] text-lifted-dim mt-0.5">Qty {it.quantity} · {money(it.unit_price)} each</div>
                      </div>
                      <div className="text-sm text-lifted-green font-bebas shrink-0 self-start">{money(it.line_total)}</div>
                    </li>
                  ))}
                </ul>
                {order.fulfillment?.tracking && (
                  <div className="mt-3 pt-3 border-t border-white/10 text-xs">
                    <div className="text-lifted-dim">Tracking</div>
                    <div className="font-mono">{order.fulfillment.tracking.company || "—"} · {order.fulfillment.tracking.number || "—"}</div>
                    {order.fulfillment.tracking.url && (
                      <a className="text-lifted-green underline" href={order.fulfillment.tracking.url} target="_blank" rel="noreferrer">Open tracking link</a>
                    )}
                  </div>
                )}
              </Card>

              {/* PAYMENT / TOTALS CARD */}
              <Card title="Payment" tid="section-payment"
                     icon={CreditCard}
                     right={<Chip small tone={PAY_TONE[order.payment?.status]}>{PAY_LABEL[order.payment?.status]}</Chip>}>
                <TotalsRow label={`Subtotal · ${(order.line_items || []).length} items`} value={order.totals?.subtotal} />
                <TotalsRow label="Shipping" value={order.totals?.shipping_total} />
                <TotalsRow label="Discount" value={order.totals?.discount_total} />
                <TotalsRow label="Tax" value={order.totals?.tax_total} />
                <TotalsRow label="Total" value={order.totals?.total} bold />
                <TotalsRow label="Paid" value={order.totals?.amount_paid} muted />
                {order.totals?.amount_refunded > 0 && <TotalsRow label="Refunded" value={order.totals.amount_refunded} muted />}
                <div className="mt-2 pt-2 border-t border-white/10 text-[0.65rem] text-lifted-dim">
                  Provider: {order.payment?.provider || "—"}
                  {order.payment?.method && <span> · {order.payment.method}</span>}
                </div>
              </Card>

              {/* CUSTOMER CARD */}
              <Card title="Customer" tid="section-customer" icon={UserIcon}>
                <div className="text-sm text-white">{order.customer?.name || <span className="italic text-lifted-dim">No name on file</span>}</div>
                <div className="text-xs mt-1 space-y-0.5">
                  <div><span className="text-lifted-dim">Email: </span>{order.customer?.email || <span className="italic text-lifted-dim">Not provided</span>}</div>
                  <div><span className="text-lifted-dim">Phone: </span>{order.customer?.phone || <span className="italic text-lifted-dim">Not provided</span>}</div>
                </div>
              </Card>

              {/* SHIPPING CARD */}
              <Card title="Shipping Address" tid="section-shipping" icon={MapPin}>
                {order.shipping_address ? (
                  <div className="text-xs space-y-0.5">
                    {order.shipping_address.name && <div className="text-white">{order.shipping_address.name}</div>}
                    {order.shipping_address.company && <div className="text-lifted-dim">{order.shipping_address.company}</div>}
                    {order.shipping_address.address1 && <div>{order.shipping_address.address1}</div>}
                    {order.shipping_address.address2 && <div>{order.shipping_address.address2}</div>}
                    <div>
                      {[order.shipping_address.city, order.shipping_address.province || order.shipping_address.province_code, order.shipping_address.zip].filter(Boolean).join(", ")}
                    </div>
                    {order.shipping_address.country && <div>{order.shipping_address.country}</div>}
                    {order.shipping_address.phone && <div className="text-lifted-dim mt-1">{order.shipping_address.phone}</div>}
                  </div>
                ) : <div className="text-xs text-lifted-dim italic">No shipping address on file.</div>}
              </Card>

              {/* ADMIN DETAILS — collapsible */}
              <div className="glass-panel border border-white/10 rounded-lg">
                <button onClick={() => setAdminExpanded(v => !v)}
                         className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-lifted-dim hover:bg-white/[0.03]"
                         data-testid="section-admin-toggle">
                  <span className="font-bebas tracking-[0.18em] uppercase">Admin Details</span>
                  {adminExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                {adminExpanded && (
                  <div className="px-4 py-3 border-t border-white/10 text-[0.65rem] font-mono text-lifted-dim space-y-0.5"
                        data-testid="section-admin">
                    <AdminKV k="LATM ID"          v={order.id} copyable />
                    <AdminKV k="Shopify Order #"  v={order.shopify_order_number} />
                    <AdminKV k="Shopify Order ID" v={order.shopify_order_id} copyable />
                    <AdminKV k="Checkout ID"      v={order.checkout_id || order.shopify_checkout_id} />
                    <AdminKV k="Payment TX ID"    v={order.payment_transaction_id} />
                    <AdminKV k="Order Source"     v={order.order_source} />
                    <AdminKV k="Created"          v={fmtDateLong(order.created_at)} />
                    <AdminKV k="Paid"             v={order.paid_at ? fmtDateLong(order.paid_at) : null} />
                    <AdminKV k="Fulfilled"        v={order.fulfillment?.fulfilled_at ? fmtDateLong(order.fulfillment.fulfilled_at) : null} />
                    <AdminKV k="Shopify Fulfillment ID" v={order.fulfillment?.shopify_fulfillment_id} />
                    {order.affiliate?.slug && <AdminKV k="Affiliate" v={order.affiliate.slug} />}
                    {order.affiliate?.referral_code && <AdminKV k="Referral" v={order.affiliate.referral_code} />}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function Card({ title, icon: Icon, tid, right, children }) {
  return (
    <section className="glass-panel border border-white/10 rounded-lg p-4" data-testid={tid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase flex items-center gap-1">
          {Icon && <Icon className="h-3 w-3" />} {title}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}
function TotalsRow({ label, value, bold, muted }) {
  if (value == null) return null;
  return (
    <div className={`flex justify-between ${bold ? "text-base font-anton pt-2 mt-1 border-t border-white/10" : "text-xs"} ${muted ? "text-lifted-dim" : ""}`}>
      <span className={bold ? "text-white" : "text-lifted-dim"}>{label}</span>
      <span className={bold ? "text-lifted-green" : ""}>{money(value)}</span>
    </div>
  );
}
function AdminKV({ k, v, copyable }) {
  if (!v) return null;
  return (
    <div className="flex items-start justify-between gap-3">
      <span><span className="text-lifted-dim mr-1">{k}:</span>{v}</span>
      {copyable && (
        <button onClick={() => navigator.clipboard?.writeText(String(v))}
                 className="text-lifted-dim hover:text-lifted-green shrink-0" aria-label="Copy">
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FULFILLMENT FORM (unchanged behavior from Iter100 — gated by env)
function FulfillmentForm({ order, onDone }) {
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [url, setUrl] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const paid = order.payment?.status === "PAID";
  const submit = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/fulfill`, {
        carrier, tracking_number: tracking, tracking_url: url,
        notify_customer: notify, force_confirm_unpaid: !paid,
      });
      setResult(data);
      if (data.ok && !data.dry_run && onDone) onDone();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-lifted-green/30 bg-lifted-green/5 rounded p-3 space-y-2" data-testid="fulfillment-form">
      {!paid && (
        <div className="text-[0.65rem] text-rose-300 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> Order is not marked PAID. Confirm you want to fulfill anyway.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[0.65rem] text-lifted-dim">
          Carrier
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)}
                   className="input-lifted w-full text-sm mt-0.5" data-testid="fulfillment-carrier">
            {["USPS","UPS","FedEx","DHL","Other"].map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-[0.65rem] text-lifted-dim">
          Tracking Number
          <input value={tracking} onChange={(e) => setTracking(e.target.value)}
                  className="input-lifted w-full text-sm mt-0.5"
                  placeholder="9400111202… / 1Z999AA…"
                  data-testid="fulfillment-tracking" />
        </label>
      </div>
      <label className="block text-[0.65rem] text-lifted-dim">
        Tracking URL (optional)
        <input value={url} onChange={(e) => setUrl(e.target.value)} className="input-lifted w-full text-sm mt-0.5"
                data-testid="fulfillment-tracking-url" />
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="fulfillment-notify" />
        Notify customer via Shopify shipment email
      </label>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || !tracking} className="btn-lifted text-xs" data-testid="fulfillment-ship">
          {notify ? "Mark Shipped + Notify Customer" : "Save Tracking + Mark Shipped"}
        </button>
      </div>
      {err && <div className="text-xs text-rose-300">{err}</div>}
      {result && (
        <div className="text-[0.65rem] font-mono border-t border-white/10 pt-1" data-testid="fulfillment-result">
          {result.dry_run ? (
            <span className="text-amber-300">ⓘ DRY RUN — Shopify writes disabled in this environment. Local tracking saved.</span>
          ) : result.already_fulfilled ? (
            <span className="text-lifted-green">✓ Already fulfilled — no duplicate created.</span>
          ) : (
            <span className="text-lifted-green">✓ Shopify fulfillment id: {result.shopify_fulfillment_id}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RECONCILE REPORT MODAL (moved out of primary UX)
function ReconcileReportModal({ onClose }) {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/orders/reconcile-report")
      .then(({ data }) => setReport(data))
      .catch(e => setErr(apiErrorMessage(e)));
  }, []);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" data-testid="recon-modal">
      <div className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-lifted-black border border-white/10 rounded-lg">
        <header className="shrink-0 px-5 py-3 flex items-center justify-between border-b border-white/10">
          <h3 className="font-anton text-xl text-white">Reconcile Report</h3>
          <button onClick={onClose} className="text-lifted-dim hover:text-white" data-testid="recon-close"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {err && <div className="text-rose-300 text-sm">{err}</div>}
          {!report ? <div className="text-lifted-dim text-sm">Loading…</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Kpi label="Confirmed"        value={report.summary?.confirmed_groups}       tone="rose" />
                <Kpi label="High Confidence"  value={report.summary?.high_confidence_groups} tone="amber" />
                <Kpi label="Needs Review"     value={report.summary?.review_groups}          tone="sky" />
                <Kpi label={`Stale Pending >${report.summary?.window_hours}h`} value={report.summary?.stale_pending_older_than_window} tone="rose" />
              </div>
              <div className="text-[0.65rem] text-lifted-dim italic">{report.note}</div>
              <ReconGroup title="CONFIRMED" tone="rose"  groups={report.confirmed || []} />
              <ReconGroup title="HIGH"      tone="amber" groups={report.high || []} />
              <ReconGroup title="REVIEW"    tone="sky"   groups={report.review || []} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function Kpi({ label, value, tone }) {
  const t = { rose: "text-rose-300", amber: "text-amber-300", sky: "text-sky-300" };
  return (
    <div className="glass-panel p-3">
      <div className="text-[0.6rem] font-bebas tracking-[0.22em] text-lifted-dim uppercase">{label}</div>
      <div className={`text-2xl font-anton ${t[tone] || "text-white"}`}>{value ?? "—"}</div>
    </div>
  );
}
function ReconGroup({ title, tone, groups }) {
  if (!groups.length) return null;
  const b = { rose: "border-rose-500/40", amber: "border-amber-400/40", sky: "border-sky-500/40" };
  return (
    <div className={`glass-panel p-3 border ${b[tone] || "border-white/10"}`} data-testid={`recon-group-${title.toLowerCase()}`}>
      <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase mb-2">{title} ({groups.length})</div>
      <ul className="space-y-1 text-xs">
        {groups.map((g, i) => (
          <li key={i} className="border-b border-white/5 pb-1">
            <span className="text-white">{g.email}</span>
            <span className="text-lifted-dim"> · ${(g.total_cents / 100).toFixed(2)}</span>
            <span className="text-lifted-dim"> · {g.count} records</span>
            <span className="text-[0.6rem] text-lifted-dim block italic">{g.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
